import { Button, Flex, Form, Input, Modal } from 'antd';
import { useEffect, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useChangeNameOrganizationOrganizationChangeNamePostMutation } from '@shared/api';
import { useNotify } from '@shared/hooks/use-notify';

import type { ProfileCompany } from '../types';
import styles from './company-edit-modal.module.scss';

interface CompanyEditModalProps {
  company?: ProfileCompany;
  open: boolean;
  onClose: () => void;
}

interface CompanyEditValues {
  title: string;
}

export const CompanyEditModal: FC<CompanyEditModalProps> = ({ company, open, onClose }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'profile.user.companies',
  });
  const { t: tCommon } = useTranslation('translation');
  const [form] = Form.useForm<CompanyEditValues>();
  const notify = useNotify();
  const [changeName, { isLoading }] = useChangeNameOrganizationOrganizationChangeNamePostMutation();

  useEffect(() => {
    if (company && open) {
      form.setFieldsValue({ title: company.title });
    }
  }, [company, form, open]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async ({ title }: CompanyEditValues) => {
    if (!company) return;

    try {
      await changeName({
        bodyChangeNameOrganizationOrganizationChangeNamePost: {
          organization_id: company.id,
          new_name: title.trim(),
        },
      }).unwrap();

      notify({ type: 'success', message: t('notifications.updated') });
      handleClose();
    } catch {
      notify({ message: t('notifications.updateError') });
    }
  };

  return (
    <Modal
      centered
      width={537}
      open={open}
      title={t('editModal.title')}
      footer={null}
      destroyOnClose
      onCancel={handleClose}
      className={styles.modal}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark
        onFinish={handleSubmit}
        className={styles.form}
      >
        <Form.Item
          name="title"
          label={t('editModal.name')}
          rules={[
            { required: true, whitespace: true, message: t('editModal.nameRequired') },
            {
              max: 32,
              message: tCommon('common.formErrors.stringMaxLen', { max: 32 }),
            },
          ]}
        >
          <Input autoFocus disabled={isLoading} size="large" />
        </Form.Item>

        <Form.Item label={t('editModal.email')} className={styles.readonlyField}>
          <Input value={company?.email || '—'} readOnly size="large" />
        </Form.Item>

        <Form.Item label={t('editModal.phone')} className={styles.readonlyField}>
          <Input value={company?.phone || '—'} readOnly size="large" />
        </Form.Item>

        <Flex gap={8} className={styles.actions}>
          <Button block onClick={handleClose} disabled={isLoading} size="large">
            {t('editModal.cancel')}
          </Button>
          <Button block type="primary" htmlType="submit" loading={isLoading} size="large">
            {t('editModal.submit')}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
